import { apiEndpoints } from "@config/env";
import { getData } from "./global";
import type { Gp50ManeuverType } from "./gp50ManeuverCheatSheetColumns";
import type {
  PerformanceDriversConditionCell,
  PerformanceDriversEnvelopeSlice,
  PerformanceDriversFit,
  PointOfSail,
  ShapBeeswarmFeature,
} from "./performanceDriversModelFit";
import {
  PERFORMANCE_DRIVERS_TWS_BINS_KPH,
  availablePerformanceDriversConfigurations,
  classifyPerformanceDriversConfidence,
  conditionKey,
  normalizeShapBeeswarmFeatures,
} from "./performanceDriversModelFit";
import type { PerformanceDriversBeeswarmKind } from "./performanceDriversFeatures";

export type { PerformanceDriversBeeswarmKind };

export const PERFORMANCE_DRIVERS_TWS_ALL = "ALL" as const;
export type PerformanceDriversTwsSelection = typeof PERFORMANCE_DRIVERS_TWS_ALL | number;

export const PERFORMANCE_DRIVERS_OBJECT_STATIC = "performance_drivers_vmg_shap";
export const PERFORMANCE_DRIVERS_OBJECT_DYNAMICS = "performance_drivers_vmg_shap_dynamics";

export function performanceDriversManeuverObjectName(type: Gp50ManeuverType): string {
  return `performance_drivers_vmg_shap_${type}`;
}

export function performanceDriversObjectNameForKind(
  kind: PerformanceDriversBeeswarmKind,
  maneuverType?: Gp50ManeuverType
): string {
  if (kind === "static") return PERFORMANCE_DRIVERS_OBJECT_STATIC;
  if (kind === "dynamics") return PERFORMANCE_DRIVERS_OBJECT_DYNAMICS;
  if (maneuverType) return performanceDriversManeuverObjectName(maneuverType);
  throw new Error("maneuverType required for maneuver beeswarm object");
}

export interface PerformanceDriversBeeswarmCell extends PerformanceDriversConditionCell {
  n_boats?: number;
  rmse?: number | null;
  rmse_train?: number | null;
  fit?: PerformanceDriversFit | null;
}

export interface PerformanceDriversBeeswarmEnvelope extends PerformanceDriversEnvelopeSlice {
  beeswarm_kind?: PerformanceDriversBeeswarmKind;
  aggregate?: string;
  maneuver_type?: string | null;
  condition_cells?: PerformanceDriversBeeswarmCell[];
}

export function performanceDriversTwsDropdownOptions(
  envelope?: PerformanceDriversEnvelopeSlice | null
): Array<typeof PERFORMANCE_DRIVERS_TWS_ALL | number> {
  const bins = envelope?.tws_bins?.length
    ? envelope.tws_bins.filter((bin) => PERFORMANCE_DRIVERS_TWS_BINS_KPH.includes(bin))
    : [...PERFORMANCE_DRIVERS_TWS_BINS_KPH];
  return [PERFORMANCE_DRIVERS_TWS_ALL, ...bins];
}

export function isPerformanceDriversTwsAll(
  selection: PerformanceDriversTwsSelection
): selection is typeof PERFORMANCE_DRIVERS_TWS_ALL {
  return selection === PERFORMANCE_DRIVERS_TWS_ALL;
}

function parseJsonIfString<T>(raw: unknown): T | null {
  if (raw == null) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }
  return raw as T;
}

export function resolvePerformanceDriversEnvelope(
  raw: unknown
): PerformanceDriversBeeswarmEnvelope | null {
  if (raw == null || typeof raw !== "object") return null;
  const outer = raw as Record<string, unknown>;
  const parsed = parseJsonIfString<PerformanceDriversBeeswarmEnvelope>(outer.json);
  const inner = parsed ?? (outer as PerformanceDriversBeeswarmEnvelope);
  if (!Array.isArray(inner.condition_cells)) return null;
  return inner;
}

export function findPerformanceDriversCell(params: {
  envelope: PerformanceDriversBeeswarmEnvelope | null | undefined;
  configuration?: string;
  twsBin: number;
  pointOfSail: string;
  schemaVersion: number;
  showConfigSelector: boolean;
}): PerformanceDriversBeeswarmCell | null {
  const { envelope, configuration, twsBin, pointOfSail, schemaVersion, showConfigSelector } =
    params;
  if (!envelope?.condition_cells?.length) return null;

  const posUpper = pointOfSail.toUpperCase();
  const key = conditionKey(
    showConfigSelector ? configuration : undefined,
    twsBin,
    pointOfSail,
    schemaVersion
  );
  const exact = envelope.condition_cells.find((cell) => cell.condition_key === key);
  if (exact) return exact;

  const byTwsAndPos = envelope.condition_cells.filter(
    (cell) =>
      cell.tws_bin === twsBin &&
      String(cell.point_of_sail ?? "").toUpperCase() === posUpper
  );
  if (byTwsAndPos.length === 0) return null;

  return (
    byTwsAndPos.find((cell) => !cell.skipped && (cell.features?.length ?? 0) > 0) ??
    byTwsAndPos[0] ??
    null
  );
}

export function resolveManeuverBeeswarmConfiguration(params: {
  maneuverEnvelope: PerformanceDriversBeeswarmEnvelope | null | undefined;
  preferredConfiguration?: string;
  schemaVersion: number;
}): { configuration?: string; showConfigSelector: boolean } {
  const { maneuverEnvelope, preferredConfiguration, schemaVersion } = params;
  if (!maneuverEnvelope || schemaVersion < 2) {
    return { showConfigSelector: false };
  }

  const configs = availablePerformanceDriversConfigurations(
    maneuverEnvelope,
    "ALL",
    schemaVersion
  );
  if (configs.length === 0) {
    return { showConfigSelector: false };
  }

  const configuration =
    preferredConfiguration && configs.includes(preferredConfiguration)
      ? preferredConfiguration
      : configs[0];

  return { configuration, showConfigSelector: true };
}

export function beeswarmFeaturesFromCell(
  cell: PerformanceDriversBeeswarmCell | null | undefined,
  displayNames?: Record<string, string>
): ShapBeeswarmFeature[] {
  if (!cell || cell.skipped || !cell.features?.length) return [];
  const normalized = normalizeShapBeeswarmFeatures(cell.features);
  if (!displayNames) return normalized;
  return normalized.map((feat) => ({
    ...feat,
    label: displayNames[feat.name] ?? feat.label,
  }));
}

export function beeswarmFitRatingFromCell(cell: PerformanceDriversBeeswarmCell | null) {
  if (!cell) return null;
  return classifyPerformanceDriversConfidence({
    n_samples: cell.n_samples,
    n_boats: cell.n_boats ?? 0,
    skipped: cell.skipped,
    skip_reason: cell.skip_reason,
    fit: cell.fit ?? null,
    rmse: cell.rmse,
    rmse_train: cell.rmse_train,
  });
}

export async function fetchPerformanceDriversEnvelopeObject(params: {
  className: string;
  projectId: string | number;
  objectName: string;
}): Promise<PerformanceDriversBeeswarmEnvelope | null> {
  const q = new URLSearchParams({
    class_name: params.className,
    project_id: String(params.projectId),
    object_name: params.objectName,
  });
  const url = `${apiEndpoints.app.projects}/object/latest?${q.toString()}`;
  const result = await getData(url);
  if (!result?.success || result.data == null) return null;
  return resolvePerformanceDriversEnvelope(result.data);
}

export function beeswarmSectionTitle(
  kind: PerformanceDriversBeeswarmKind,
  twsBin: number,
  pointOfSail: string,
  maneuverLabel?: string
): string {
  if (kind === "maneuver") {
    const plural =
      maneuverLabel === "TACK" ? "Tacks" : maneuverLabel === "GYBE" ? "Gybes" : maneuverLabel ?? "Maneuvers";
    return `Maneuvers - ${plural} ${twsBin} kph`;
  }
  return `${kind === "dynamics" ? "Dynamics" : "Straight line"} · ${pointOfSail} · ${twsBin} kph`;
}

export function beeswarmMissingSnapshotMessage(
  kind: PerformanceDriversBeeswarmKind,
  maneuverLabel?: string
): string {
  if (kind === "dynamics") {
    return "No dynamics SHAP snapshot found. Run condition_model.py with kind dynamics or all to build performance_drivers_vmg_shap_dynamics.";
  }
  if (kind === "maneuver") {
    return `No ${maneuverLabel ?? "maneuver"} SHAP snapshot found. Run condition_model.py with kind maneuvers or all to build the maneuver object.`;
  }
  return "No straight-line SHAP data for this cell.";
}

export function effectiveBeeswarmPointOfSail(
  kind: PerformanceDriversBeeswarmKind,
  pointOfSail: PointOfSail
): string {
  return kind === "maneuver" ? "ALL" : pointOfSail;
}
